import React, { useEffect, useMemo, useState, ViewTransition } from 'react';
import { View, Text, Pressable, Image, TextInput, Modal } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import moment from 'moment';
import XLSX from 'xlsx';

// The teams' full names, shown on the team step and in its picker in place of the codes
const TEAM_DISPLAY_NAMES = {
    'NOR': 'Northern',
    'SWT': 'Southwest',
    'EASTERN': 'Eastern',
    'MCLEANS': 'Mcleans',
    'GULF COAST': 'Gulf Coast',
    'PICKETT': 'Pickett',
    'ED': 'Energy Delivery'
};

// The forecast date, team and job pickers open as a drop-down under their step
// in the top bar, over the page, instead of replacing the page.
const PickerPopover = ({ left, children }) => (
    <View style={{
        position: 'absolute', top: 0, left, zIndex: 300,
        background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 14, boxShadow: 'var(--shadow-md)',
        padding: 16, maxHeight: '75vh', overflow: 'auto', maxWidth: 'calc(100vw - 320px)',
    }}>
        {children}
    </View>
);
import {
    businessUnitsAtom,
    jobBillingsAtom,
    jobOpenCommitmentsAtom,
    jobCostDetailAtom,
    jobCostDetailCondensedAtom,
    projectCompleteAtom,
    mechanicallyCompleteAtom,
    startDateActualAtom,
    dataSourceVersionAtom,
    dataSourceTeamsAtom,
    dataSourceJobsAtom,
    dataSourceJobsClosedAtom,
    dataSourceJobsInventoryAtom,
    selectedWIPAtom,
    selectedJobAtom,
    selectedTeamAtom,
    selectedVersionAtom,
    selectedJobDataAtom,
    jobUserDataAtom,
    phasedForecastAtom,
    isLiveVersionAtom,
    selectedVersionLockedAtom,
    userRoleAtom,
    userEmailCheckAtom,
    userIdAtom,
    previewRoleAtom,
    k2DataAtom,
    jobHEETDataAtom,
    versionUnlocksAtom,
    userNameAtom,
} from '../store/company';
import {
    setSelectedVersionStorage,
    setSelectedTeamStorage,
    setSelectedJobStorage,
    setSelectedJobDetailsStorage,
    updateUserDataArray,
    loadJobsClosed,
    loadJobsAll,
    loadVersionTeamJob,
    setClosedJobMode,
    isProjectControlsLead,
    isFusionAdmin,
    canPreviewRoles,
    previewableRoles,
} from '../db/company';
import { apiStatusAtom } from '../store/apiStatus';
import { ALL_JOBS, activeVersionUnlock, applyVersionUnlock, endOfToday, saveVersionUnlock } from '../db/versionUnlock';
import ApiStatusIndicator from './ApiStatusIndicator';
import UserProfile from './UserProfile';
import download from '../assets/download.svg';
import calendar from '../assets/CalendarOutlined.svg';

const DateInput = ({ value, onValueChange, onBlur }) => {
    const [date, setDate] = useState(value);

    useEffect(() => {
        setDate(value);
    }, [value]);

    const handleChange = (e) => {
        const inputValue = e.target.value;
        if (inputValue) {
            setDate(inputValue);
            if (moment(inputValue, 'YYYY-MM-DD', true).isValid()) {
                if (moment(inputValue).isAfter('2020-01-01') && moment(inputValue).isBefore('2040-01-01')) {
                    onValueChange(inputValue);
                } else {
                    onValueChange('2025-01-01');
                }
            }
        }
    };

    // Format value for input - ensure it's YYYY-MM-DD format
    const inputValue = value ? moment(value).format('YYYY-MM-DD') : '';

    return (
        <input
            type="date"
            value={inputValue}
            onChange={handleChange}
            onBlur={onBlur}
            style={{
                width: 130,
                height: 36,
                fontSize: 12,
                fontWeight: 600,
                padding: '4px 6px',
                borderRadius: 6,
                border: '1px solid #ccc',
            }}
        />
    );
};

export default function TopMenu(props) {
    //console.log('TopMenu rendering, props.photo:', props?.photo);
    
    const [businessUnits, setBusinessUnits] = useAtom(businessUnitsAtom);
    const [jobBillings] = useAtom(jobBillingsAtom);
    const [jobOpenCommitments, setJobOpenCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [jobCostDetail, setJobCostDetail] = useAtom(jobCostDetailAtom);
    const [jobCostDetailCondensed, setJobCostDetailCondensed] = useAtom(jobCostDetailCondensedAtom);
    const [projectComplete, setProjectComplete] = useAtom(projectCompleteAtom);
    const [downloadingExcel, setDownloadingExcel] = useState(false);
    const [mechanicallyCompleteGlobal, setMechanicallyCompleteGlobal] = useAtom(mechanicallyCompleteAtom);
    const [startDateActualGlobal, setStartDateActualGlobal] = useAtom(startDateActualAtom);
    const [versions, setVersions] = useAtom(dataSourceVersionAtom);
    const [teams, setTeams] = useAtom(dataSourceTeamsAtom);
    const [jobs, setJobs] = useAtom(dataSourceJobsAtom);
    const [jobsClosed, setJobsClosed] = useAtom(dataSourceJobsClosedAtom);
    const [jobsInventory, setJobsInventory] = useAtom(dataSourceJobsInventoryAtom);
    const [selectedWIP] = useAtom(selectedWIPAtom);
    const [selectedJob, setSelectedJob] = useAtom(selectedJobAtom);
    const [selectedTeam, setSelectedTeam] = useAtom(selectedTeamAtom);
    const [selectedVersion, setSelectedVersion] = useAtom(selectedVersionAtom);
    const [selectedJobDetails, setSelectedJobDetails] = useAtom(selectedJobDataAtom);
    const [jobUserData, setJobUserData] = useAtom(jobUserDataAtom);
    const [regular, setRegular] = useAtom(phasedForecastAtom);
    const [isLiveVersion] = useAtom(isLiveVersionAtom);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lockedVersion] = useAtom(selectedVersionLockedAtom);
    const [userRole] = useAtom(userRoleAtom);
    const [userEmailCheck] = useAtom(userEmailCheckAtom);
    const [userId] = useAtom(userIdAtom);
    const [previewRole, setPreviewRole] = useAtom(previewRoleAtom);
    const [k2Data, setK2Data] = useAtom(k2DataAtom);
    const [, setJobHEETData] = useAtom(jobHEETDataAtom);
    const [apiStatus] = useAtom(apiStatusAtom);

    const [isFocus, setIsFocus] = useState(false);
    const [isFocusJob, setIsFocusJob] = useState(false);
    const [isFocusClosedJob, setIsFocusClosedJobs] = useState(false);
    const [isFocusTeam, setIsFocusTeam] = useState(false);

    const [dropdownVersion, setDropdownVersion] = useState([]);
    const [dropdownJobs, setDropdownJobs] = useState([]);
    const [dropdownJobsClosed, setDropdownJobsClosed] = useState([]);
    const [dropdownJobsInventory, setDropdownJobsInventory] = useState([]);
    const [dropdownTeam, setDropdownTeam] = useState([]);

    const [versionInfo, setVersionInfo] = useState({});
    const isMarch19 = versionInfo?.Version_Start_DateTime
        ? moment(versionInfo.Version_Start_DateTime).format('MM-DD') === '03-19'
        : false;
    const isHighlightedVersion = isLiveVersion || isMarch19;
    const [buTeams, setBuTeams] = useState({});
    const [jobSections, setJobSections] = useState({});
    const [searchJob, setSearchJob] = useState('');
    const [searchableJobs, setSearchableJobs] = useState([]);
    const [searchClosedJob, setSearchClosedJob] = useState('');
    const [searchableClosedJobs, setSearchableClosedJobs] = useState([]);
    const [searchInventoryJob, setSearchInventoryJob] = useState('');
    const [searchableInventoryJobs, setSearchableInventoryJobs] = useState([]);
    const [liveVersionId, setLiveVersionId] = useState(null);
    const [closedJobsExpanded, setClosedJobsExpanded] = useState(false);
    const [expandedJob, setExpandedJob] = useState(false);

    const [pageWidth, setPageWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : 1200
    );

    // Clear search when job menu closes (selectedJob becomes null)
    // Also reset local date states so they re-sync from jobUserData on job switch
    useEffect(() => {
        if (!selectedJob) {
            setSearchJob('');
        }
        setCompletionDateForecast(undefined);
        setStartDateActual('');
        setMechanicallyComplete('');
    }, [selectedJob]);

    const [startDateActual, setStartDateActual] = useState('');
    const [completionDateForecast, setCompletionDateForecast] = useState();
    const [mechanicallyComplete, setMechanicallyComplete] = useState('');

    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showDateModal, setShowDateModal] = useState(false);

    // Which picker drop-down is open: 'version' | 'team' | 'job' | null. The first
    // step that still needs a choice opens by itself, as the pickers used to appear;
    // a click outside the top bar closes it.
    const [openPicker, setOpenPicker] = useState(null);
    useEffect(() => {
        if (props.photo) return;
        setOpenPicker(!selectedVersion ? 'version' : !selectedTeam ? 'team' : !selectedJob ? 'job' : null);
    }, [selectedVersion, selectedTeam, selectedJob, props.photo]);
    useEffect(() => {
        if (!openPicker) return;
        const close = (e) => { if (!e.target.closest || !e.target.closest('[data-picker-bar]')) setOpenPicker(null); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [openPicker]);
    const [tempStartDate, setTempStartDate] = useState('');
    const [tempEndDate, setTempEndDate] = useState('');
    const [tempMechComplete, setTempMechComplete] = useState('');
    const toggleDatePicker = () => setShowDatePicker((s) => !s);

    useEffect(() => {
        const onResize = () => setPageWidth(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // Closed jobs come from the newest version, not the selected one: the
    // active job list is loaded before the saved version is restored, so its
    // query falls back to the newest version. Reading closed jobs from an older
    // selected version left a gap — a job closed since then was in neither list
    // (Yadira 10/22: 54026046 and 54026053, closed in v125, missing on v124).
    useEffect(() => {
        if (liveVersionId && (!jobsClosed || jobsClosed.length === 0)) {
            setJobsClosed([{}]);
            loadJobsClosed({ forecastGuid: liveVersionId }).then((data) => {
                setJobsClosed(data?.data || []);
            });
        }
    }, [liveVersionId, jobsClosed]);

    const isPickettTeam = selectedTeam === 'PICKETT' || selectedTeam === 'Pickett';
    const [inventoryLoading, setInventoryLoading] = useState(false);
    const [inventoryFetchedAt, setInventoryFetchedAt] = useState(null);

    // Fetch inventory (JP) jobs on demand the first time Pickett is selected.
    // Source: readJobInfoAll (already deployed) -> filter BU_TYPE_CODE === 'JP'.
    useEffect(() => {
        if (!isPickettTeam) return;
        if (jobsInventory && jobsInventory.length > 0) return;
        if (inventoryLoading) return;
        console.log('[inventory] fetching JP jobs via readJobInfoAll...');
        setInventoryLoading(true);
        loadJobsAll().then((data) => {
            const all = Array.isArray(data?.data) ? data.data : [];
            const jp = all.filter((j) => j && j.BU_TYPE_CODE === 'JP');
            console.log('[inventory] JP jobs loaded:', jp.length, 'of', all.length, 'total');
            setJobsInventory(jp);
            setInventoryFetchedAt(Date.now());
        }).catch((err) => {
            console.error('[inventory] failed to load inventory jobs', err);
        }).finally(() => {
            setInventoryLoading(false);
        });
    }, [isPickettTeam]);

    useEffect(() => {
        if (versions && versions.length > 0 && selectedVersion) {
            setVersionInfo(versions.find((v) => v.Version_GUID === selectedVersion) || {});
        }
    }, [versions, selectedVersion]);

    useEffect(() => {
        if (jobUserData && jobUserData.length > 0) {
            let hasCompletionDate = false;
            jobUserData.forEach((data) => {
                if (data.Forecast_Field_Name === 'startDateActual') {
                    setStartDateActual(data.Forecast_Value);
                    setStartDateActualGlobal(data.Forecast_Value);
                }
                if (data.Forecast_Field_Name === 'mechanicallyComplete') {
                    setMechanicallyComplete(data.Forecast_Value);
                    setMechanicallyCompleteGlobal(data.Forecast_Value);
                }
                if (data.Forecast_Field_Name === 'completionDateForecast') {
                    setCompletionDateForecast(data.Forecast_Value);
                    setProjectComplete(data.Forecast_Value);
                    hasCompletionDate = true;
                }
            });
            // Fall back to Planned_Complete_Date if no user-saved forecast date
            if (!hasCompletionDate && selectedJobDetails?.Planned_Complete_Date) {
                const fallback = moment(selectedJobDetails.Planned_Complete_Date).format('YYYY-MM-DD');
                setCompletionDateForecast(fallback);
                setProjectComplete(fallback);
            }
        }
    }, [jobUserData, setProjectComplete]);

    useEffect(() => {
        if (versions && versions.length > 0) {
            const allowed = ['WIP', 'Regular', 'Reg-locked', 'Unassigned', 'WIP-locked'];
            const mapped = versions
                .filter((v) => allowed.includes(v.Version_Type))
                .map((v) => ({
                    label:
                        moment(v.Version_Start_DateTime).format('MMM D, YYYY') +
                        (v.Version_Type.includes('WIP') ? ' - WIP' : ''),
                    labelDate: moment(v.Version_Start_DateTime).format('MM/DD/YYYY HH:mm:ss'),
                    value: v.Version_GUID,
                }))
                .sort((b, a) => new Date(a.labelDate) - new Date(b.labelDate));
            setDropdownVersion(mapped);

            // Compare full timestamps: two versions built the same day must
            // still resolve to the later one.
            let latestDate = moment('2024-01-01');
            let latest = null;
            versions.forEach((v) => {
                if (moment(v.Version_Start_DateTime).isAfter(latestDate)) {
                    latestDate = moment(v.Version_Start_DateTime);
                    latest = v.Version_GUID;
                }
            });
            setLiveVersionId(latest);
        }
    }, [versions]);

    useEffect(() => {
        const filtered = jobs && jobs.length ? jobs.filter((j) => j.Cat_Code_BU_27 === selectedTeam) : [];
        if (filtered && filtered.length > 0) {
            setDropdownJobs(
                filtered.map((v) => ({
                    label: `${v.BU_ID} ${v.BU_Description}`,
                    value: v.BU_ID,
                    data: v,
                }))
            );
        } else {
            setDropdownJobs([]);
        }
    }, [jobs, selectedTeam]);

    useEffect(() => {
        const list = (jobsClosed || []).map((v) => ({
            label: `${v.BU_ID} ${v.BU_Description}`,
            value: v.BU_ID,
            data: v,
        }));
        setDropdownJobsClosed(list);
    }, [jobsClosed]);

    useEffect(() => {
        const list = (jobsInventory || [])
            .filter((v) => v && v.BU_ID)
            .map((v) => ({
                label: `${v.BU_ID} ${v.BU_Description || ''}`,
                value: v.BU_ID,
                data: v,
            }));
        setDropdownJobsInventory(list);
    }, [jobsInventory]);

    useEffect(() => {
        if (jobs && jobs.length) {
            // Get all unique team codes from jobs
            const teamsFromJobs = {};
            jobs.forEach(job => {
                const teamCode = job.Cat_Code_BU_27;
                if (teamCode && !teamsFromJobs[teamCode]) {
                    teamsFromJobs[teamCode] = teamCode;
                }
            });
            
            // Create dropdown with all teams that have jobs
            setDropdownTeam(
                Object.keys(teamsFromJobs)
                    .sort()
                    .map((teamCode) => ({
                        label: teamCode === 'ED' ? 'Energy Delivery' : teamCode,
                        value: teamCode
                    }))
            );
        } else {
            setDropdownTeam([]);
        }
    }, [jobs]);

    useEffect(() => {
        let list = [];
        if (selectedTeam) list = jobSections[selectedTeam] || [];
        if (searchJob) {
            list = list.filter(
                (job) =>
                    job.BU_Description?.toLowerCase().includes(searchJob.toLowerCase()) ||
                    job.BU_ID?.toLowerCase().includes(searchJob.toLowerCase())
            );
        }
        setSearchableJobs(list);
    }, [searchJob, selectedTeam, selectedJob, jobSections]);

    useEffect(() => {
        let list = dropdownJobsClosed || [];
        if (searchClosedJob) {
            list = list.filter(
                (job) =>
                    job.data?.BU_Description?.toLowerCase().includes(searchClosedJob.toLowerCase()) ||
                    job.data?.BU_ID?.toLowerCase().includes(searchClosedJob.toLowerCase()) ||
                    job.label?.toLowerCase().includes(searchClosedJob.toLowerCase())
            );
        }
        setSearchableClosedJobs(list);
    }, [searchClosedJob, dropdownJobsClosed]);

    useEffect(() => {
        let list = dropdownJobsInventory || [];
        if (searchInventoryJob) {
            list = list.filter(
                (job) =>
                    job.data?.BU_Description?.toLowerCase().includes(searchInventoryJob.toLowerCase()) ||
                    job.data?.BU_ID?.toLowerCase().includes(searchInventoryJob.toLowerCase()) ||
                    job.label?.toLowerCase().includes(searchInventoryJob.toLowerCase())
            );
        }
        setSearchableInventoryJobs(list);
    }, [searchInventoryJob, dropdownJobsInventory]);

    useEffect(() => {
        const teamCodeMap = {};
        const jobSectionsLocal = {};
        if (teams && teams.length) {
            teams.forEach((t) => {
                teamCodeMap[t.Cat_Code_BU_21_Description] = t.Cat_Code_BU_21;
            });
            setBuTeams(teamCodeMap);
        }

        const teamsMapping = {
            '101': 'STX',
            '116': 'PICKETT',
            '120': 'CUSHING',
            '121': 'PASSTHRU',
            '122': 'Pickett',
            '124': 'GULF COAST',
            '135': 'FACILITIES',
            '153': 'Facilities',
            '155': 'TERMINALS',
            '161': 'BOLT',
            '502': 'NOR',
            '504': 'ETX',
            '540': 'SWT',
            BOLT: 'BOLT',
            CAPSTONE: 'CAPSTONE',
            CUSHING: 'CUSHING',
            MCLEANS: 'MCLEANS',
            DELTA: 'DELTA',
            EASTERN: 'EASTERN',
            EPS: 'EPS',
            ETX: 'ETX',
            FACILITIES: 'FACILITIES',
            'GULF COAST': 'GULF COAST',
            NOR: 'NOR',
            PASSTHRU: 'PASSTHRU',
            PICKETT: 'PICKETT',
            PIPELINE: 'PIPELINE',
            SE: 'SE',
            STX: 'STX',
            TERMINALS: 'TERMINALS',
            SWT: 'SWT',
            WTX: 'WTX',
        };

        if (jobs && jobs.length) {
            jobs.forEach((j) => {
                // if(j.Cat_Code_BU_27 == 'CUSHING' || j.Cat_Code_BU_27 == 'MCLEANS') {
                //     console.log('Processing job for team mapping:', j.BU_ID, j.Cat_Code_BU_27, teamsMapping, jobSectionsLocal);
                // }
                const team = teamsMapping[j?.Cat_Code_BU_27] || j?.Cat_Code_BU_27;
                if (!jobSectionsLocal[team]) jobSectionsLocal[team] = [j];
                else jobSectionsLocal[team].push(j);
                // SWT jobs should also appear under WTX team (West Texas Region)
                if (team === 'SWT') {
                    if (!jobSectionsLocal['WTX']) jobSectionsLocal['WTX'] = [j];
                    else jobSectionsLocal['WTX'].push(j);
                }
            });

            const teamInfo = Object.keys(jobSectionsLocal).map((key) => ({
                short: key.length > 3 ? key.substring(0, 4) : key,
                shortName: key,
                long: teamCodeMap[key],
            }));

            const sorted = teamInfo.sort((a, b) => a.shortName.localeCompare(b.shortName));
            setBusinessUnits(sorted);
            console.log('job sections set:', jobSectionsLocal);
            setJobSections(jobSectionsLocal);
        }
    }, [jobs, teams, setBusinessUnits]);

    useEffect(() => {
        if (!selectedJob) return;
        const isClosed = dropdownJobsClosed.some((j) => j.value === selectedJob);
        setClosedJobMode(isClosed);
        if (isClosed) setClosedJobsExpanded(true);
    }, [selectedJob, dropdownJobsClosed]);

    const styles = useMemo(
        () => ({
            dropdownBox: {
                marginTop: 8,
                height: 36,
                borderColor: '#0b142e',
                background: '#0b142e',
                color: 'white',
                borderWidth: 1,
                borderRadius: 8,
                padding: '0 8px',
                marginLeft: 20,
                marginRight: 20,
                width: 175,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            },
            inputBox: {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                border: '1px solid #D9D9D9',
                height: 36,
                padding: '4px 8px',
                borderRadius: 6,
                background: '#fff',
                cursor: 'pointer',
            },
            infoRow: {
                display: 'flex',
                flexDirection: 'row',
                marginTop: 6,
                gap: 12,
            },
            span: {
                width: 220,
                fontSize: 16,
                padding: 2,
            },
        }),
        []
    );

    // --- PCL forecast lock ---------------------------------------------------
    // Project Controls Leads can freeze a job's forecast on the LIVE version
    // after the review meeting so nobody edits it before the PSR. The lock is a
    // single 'versionLocked' row saved to user data (keyed by job + version);
    // main.js reads it and flips the global lock atom that blocks all saves.
    const isPCL = isProjectControlsLead(userRole, userEmailCheck);

    // Admin "View as role" switcher — moved here from the side menu. Gated on
    // real identity (never the previewed role) so a preview can always be exited.
    const canPreview = canPreviewRoles(userEmailCheck, userId);

    // Set/clear the preview role and mirror it to localStorage so it survives reloads.
    const applyPreviewRole = (role) => {
        setPreviewRole(role);
        try {
            if (role) localStorage.setItem('previewRole', role);
            else localStorage.removeItem('previewRole');
        } catch (e) { }
    };
    // Locked when the row's value matches the version being viewed. The value
    // stores the Version_GUID the lock was placed on (not 'true') so that when
    // the PSR copy job carries the row to the next version, the stale version
    // number no longer matches and the new version starts unlocked. Legacy
    // 'true' rows (written before version-stamping) still count as locked.
    const jobLocked = useMemo(
        () => Array.isArray(jobUserData) && jobUserData.some(
            (d) => d.Forecast_Field_Name === 'versionLocked' &&
                (String(d.Forecast_Value) === 'true' || String(d.Forecast_Value) === String(selectedVersion))
        ),
        [jobUserData, selectedVersion]
    );

    const setJobLock = (locked) => {
        if (!selectedJob || !selectedVersion) return;
        const value = locked ? String(selectedVersion) : 'false';
        // lockedVersion:false so this write is never blocked by its own lock.
        updateUserDataArray({
            tab: 'All',
            rowId: 'versionLock',
            fieldName: 'versionLocked',
            value,
            id: selectedJob,
            version: String(selectedVersion || ''),
            lockedVersion: false,
        });
        // Optimistically update local user data so the freeze/unfreeze takes
        // effect immediately (main.js recomputes the lock from jobUserData).
        setJobUserData((prev) => {
            const rows = Array.isArray(prev) ? [...prev] : [];
            const idx = rows.findIndex(
                (r) => r.Forecast_Field_Name === 'versionLocked' && r.Forecast_Tab === 'All'
            );
            if (idx >= 0) rows[idx] = { ...rows[idx], Forecast_Value: value };
            else rows.push({
                Forecast_Tab: 'All',
                Forecast_Field_Name: 'versionLocked',
                Forecast_Value: value,
                Forecast_Row_ID: 'versionLock',
                BU_ID: selectedJob,
                Version_GUID: selectedVersion,
            });
            return rows;
        });
    };

    const toggleJobLock = () => {
        if (jobLocked) {
            if (window.confirm('Unlock this forecast? Editors will be able to change it again before the PSR.')) {
                setJobLock(false);
            }
        } else {
            if (window.confirm('Lock this forecast for the current live version? It stays frozen (no edits by anyone) until you unlock it or the PSR creates a new version.')) {
                setJobLock(true);
            }
        }
    };

    // --- Admin version unlock --------------------------------------------------
    // Every version but the live one is locked (main.js). An admin can open the
    // one being viewed back up for this job or for all jobs until 11:59 PM; it
    // locks again on its own after that (db/versionUnlock.js). Everyone viewing
    // an unlocked version sees the tag, because edits made there stay on that
    // version and don't carry into the live one.
    const isAdmin = isFusionAdmin(userId, userEmailCheck, userRole);
    const [versionUnlocks, setVersionUnlocks] = useAtom(versionUnlocksAtom);
    const [userName] = useAtom(userNameAtom);
    const unlockBy = userName || userEmailCheck || '';
    const versionUnlock = activeVersionUnlock(versionUnlocks, selectedVersion, selectedJob);
    const unlockScope = versionUnlock ? (versionUnlock.job === ALL_JOBS ? 'all' : 'job') : 'locked';
    const versionLabel = versionInfo?.Version_Start_DateTime
        ? moment(versionInfo.Version_Start_DateTime).format('MMM D')
        : `version ${selectedVersion}`;

    const setUnlockScope = (scope) => {
        if (scope === unlockScope || !selectedVersion || !selectedJob) return;
        const ok = scope === 'locked'
            ? window.confirm(`Lock ${versionLabel} again${versionUnlock.job === ALL_JOBS ? ' for all jobs' : ` for job ${selectedJob}`}?`)
            : window.confirm(
                `Unlock ${versionLabel} for ${scope === 'all' ? 'all jobs' : `job ${selectedJob}`} until 11:59 PM tonight?\n\n` +
                `Edits made on ${versionLabel} stay on ${versionLabel}. They don't carry into the live version.`
            );
        if (!ok) return;
        const changes = [];
        if (versionUnlock) changes.push({ version: selectedVersion, job: versionUnlock.job, until: null, by: unlockBy });
        if (scope !== 'locked') {
            changes.push({ version: selectedVersion, job: scope === 'all' ? ALL_JOBS : selectedJob, until: endOfToday(), by: unlockBy });
        }
        changes.forEach((c) => saveVersionUnlock(c));
        setVersionUnlocks((prev) => changes.reduce(applyVersionUnlock, prev));
    };

    const saveObject = (fieldName, value) => {
        if (!value) return;
        const data = {
            userId: '154045',
            userName: 'Seth Bailey',
            fieldName,
            tab: 'All',
            rowId: 1,
            version: String(selectedVersion || ''),
            value,
            id: selectedJob,
            lockedVersion: lockedVersion,
            projectStart: startDateActual,
            projectComplete: fieldName === 'completionDateForecast' ? value : projectComplete,
        };
        updateUserDataArray(data);
    };

    const DateRangePicker = () => {
        const formatDisplay = (start, end) => {
            if (!end) return 'Select date range';
            // Year always shows on the end date; on the start too when the job spans years
            const spansYears = start && moment(start).year() !== moment(end).year();
            const startObj = start ? moment(start).format(spansYears ? 'MMM D, YYYY' : 'MMM D') : 'Start';
            const endObj = moment(end).format('MMM D, YYYY');
            return `${startObj} — ${endObj}`;
        };

        const openDateModal = () => {
            setTempStartDate(startDateActual);
            setTempEndDate(completionDateForecast);
            setTempMechComplete(mechanicallyComplete);
            setShowDateModal(true);
        };

        const closeDateModal = () => {
            setShowDateModal(false);
        };

        const saveDatesFromModal = () => {
            if (tempStartDate && tempStartDate !== startDateActual) {
                setStartDateActual(tempStartDate);
                saveObject('startDateActual', tempStartDate);
                setStartDateActualGlobal(tempStartDate);
            }
            if (tempEndDate && tempEndDate !== completionDateForecast) {
                setCompletionDateForecast(tempEndDate);
                setProjectComplete(tempEndDate);
                saveObject('completionDateForecast', tempEndDate);
            }
            if (tempMechComplete !== mechanicallyComplete) {
                setMechanicallyComplete(tempMechComplete);
                saveObject('mechanicallyComplete', tempMechComplete);
                setMechanicallyCompleteGlobal(tempMechComplete);
            }
            setShowDateModal(false);
        };

        return (
            <View>
                <Pressable
                    style={styles.inputBox}
                    onPress={openDateModal}
                >
                    <Image source={calendar} style={{ width: 16, height: 16, marginRight: 8 }} />
                    <Text style={{ fontSize: 13, color: '#444', whiteSpace: 'nowrap' }}>
                        {formatDisplay(startDateActual, completionDateForecast)}
                    </Text>
                </Pressable>

                <Modal
                    visible={showDateModal}
                    transparent={true}
                    onRequestClose={closeDateModal}
                >
                    <View style={{ 
                        flex: 1, 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)'
                    }}>
                        <View style={{ 
                            backgroundColor: 'white', 
                            borderRadius: 8, 
                            padding: 24,
                            minWidth: 320,
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                        }}>
                            <Text style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
                                Select Date Range
                            </Text>
                            
                            <View style={{ marginBottom: 16 }}>
                                <Text style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                                    Start Date - Actual
                                </Text>
                                <DateInput
                                    value={tempStartDate}
                                    onValueChange={setTempStartDate}
                                    onBlur={() => {}}
                                />
                            </View>

                            <View style={{ marginBottom: 16 }}>
                                <Text style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                                    Complete Date - Forecast
                                </Text>
                                <DateInput
                                    value={tempEndDate}
                                    onValueChange={setTempEndDate}
                                    onBlur={() => {}}
                                />
                            </View>

                            <View style={{ marginBottom: 20 }}>
                                <Text style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                                    Mech Complete
                                </Text>
                                <DateInput
                                    value={tempMechComplete}
                                    onValueChange={setTempMechComplete}
                                    onBlur={() => {}}
                                />
                            </View>

                            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12 }}>
                                <Pressable
                                    onPress={closeDateModal}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: 4,
                                        backgroundColor: '#f3f3f3',
                                    }}
                                >
                                    <Text style={{ fontSize: 13, fontWeight: 500 }}>Cancel</Text>
                                </Pressable>
                                <Pressable
                                    onPress={saveDatesFromModal}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: 4,
                                        backgroundColor: '#0078d4',
                                    }}
                                >
                                    <Text style={{ fontSize: 13, fontWeight: 500, color: 'white' }}>Save</Text>
                                </Pressable>
                            </View>
                        </View>
                    </View>
                </Modal>
            </View>
        );
    };

    const createExcel = async () => {
        setDownloadingExcel(true);
        try {
            const workbook = XLSX.utils.book_new();

            console.log('Creating Excel file with data:', jobCostDetail)

            const formattedData = jobCostDetail.map(value => ({
                "Job Number": value.Job_ID,
                "Phase Code": value.Phase_Code,
                "Phase Description": value.Phase_Code_Description,
                "Cost Type ID": value.Cost_Type_Code,
                "Cost Type Description": value.Cost_Type_Code_Description,
                "Transaction Date": value.GL_Date,
                "Job Cost": value.Job_Cost,
                "Transaction Description": value.Transaction_Description,
                "Consolidated Date": value.Consolidated_Date,
                "GL Date": value.GL_Date,
                "Vendor Name": value.Supplier_Name,
                "Total Hours": value.Total_Hours,
                "Check Number": '',
                "PO Number": value.PO_Number,
                "Division": value.Division,
                "Division Name": value.Division_Description,
                "BU Code": value.Business_Unit,
                "BU Name": value.Business_Unit_Description,
                "Cost Type Category": '',
                "Invoice Number": value.Supplier_Invoice_Number,
                "Employee ID": value.Staff_ID,
                "Unit Number": value.Unit_Number,
                "Equipment Classification": value.Equipment_Classification_Tier_Name,
                "Tax Flag": value.Is_Tax,
                "Line_ID": value.Line_ID,
                "Ticket Number": value.Ticket_Number,
                "is_CC_Transaction": value.Is_Credit_Card_Transaction,
                "Labor Classification": value.Labor_Classification,
                "Cost Type w/ Descript.": value.Cost_Type_Code + ' - ' + value.Cost_Type_Code_Description,
                "Phase Code w/ Descript.": value.Phase_Code + ' - ' + value.Phase_Code_Description
            }));

            const formattedOpen = jobOpenCommitments.map(value => ({
                "Vendor Number": value.Supplier_Number,
                "Vendor Name": value.Supplier_AB_Name,
                "Vendor Type": value.Supplier_Type,
                "Phase Code": value.Phase_Code,
                "Cost Type Id": value.Cost_Type_Code,
                "Po Ordered Amount": value.PO_Ordered_Amount,
                "Po Received Cost": value.PO_Received_Amount,
                "Po Open Amount": value.PO_Open_Amount,
                "Tax Committment": value.PO_Tax_Commitment_Amount,
                "Tax Relieved": value.PO_Tax_Relieved_Amount,
                "Tax Open Amount": value.PO_Tax_Open_Amount,
                "True Open Amount": value.PO_True_Open_Amount,
                "Ordered Quantity": value.Ordered_Quantity,
                "Ordered Quantity Received": value.Ordered_Quantity_Received,
                "Ordered Quantity Open": value.Ordered_Quantity_Open,
                "Gl Date": value.GL_Date,
                "Order Type": '',
                "Po Type": value.PO_Type,
                "Po Line No": value.PO_Line_Number,
                "Line Type": value.PO_Line_Type,
                "Po Next Status": '',
                "Po Last Status": '',
                "Next Status": value.Status_Code_Next_Description,
                "Cost Type w/Descript.": value.Cost_Type_Code + ' - ' + value.Cost_Type_Code_Description,
            }));

            const formattedBilling = jobBillings.map(value => ({
                "GL Period": value.GL_Date,
                "Company Code": value.Company_Code,
                "Client Code": value.Client_Address_Number,
                "Client Name": value.Client_Name,
                "Invoice Or Transaction": value.Invoice_Number,
                "Transaction Type": value.Invoice_Type,
                "GL Account": value.Account_ID,
                "Job Number": value.Job_Number,
                "Invoice Date": value.Invoice_Date,
                "GL Year": value.GL_Fiscal_CCYY,
                "Invoice Amount": value.Invoice_Amount,
                "Sales Tax Amount": value.Sales_Tax_Amount,
                "Retention Amount": value.Retention_Amount,
                "Invoice Type": value.Invoice_Type
            }));

            // Create worksheets from the arrays
            const costHistorySheet = XLSX.utils.json_to_sheet(formattedData);
            const openCommitmentsSheet = XLSX.utils.json_to_sheet(formattedOpen);
            const billingsSheet = XLSX.utils.json_to_sheet(formattedBilling);

            // Append the worksheets to the workbook with corresponding labels
            XLSX.utils.book_append_sheet(workbook, costHistorySheet, 'Cost History');
            XLSX.utils.book_append_sheet(workbook, openCommitmentsSheet, 'Open Commitments');
            XLSX.utils.book_append_sheet(workbook, billingsSheet, 'Billings');

            // Write the workbook to a file
            XLSX.writeFile(workbook, `${selectedJob}_data.xlsx`);
        } catch (error) {
            console.error('Error creating Excel file:', error);
        } finally {
            setDownloadingExcel(false);
        }
    };

    return (
        <View data-print-hide="true" data-picker-bar="true">
            <View
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    minWidth: props.photo ? 900 : pageWidth - 300,
                    alignItems: 'center',
                    gap: 12,
                    padding: 4,
                    background: '#fff',
                    paddingLeft: 18,
                    paddingRight: 18
                }}
            >
                {/* Left side - main content */}
                <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 }}>
                    {props.photo ? (
                        <View style={{ display: 'flex', flexDirection: 'row', gap: 20 }}>
                            <View>
                                <Text style={{ fontSize: 20, fontWeight: 600 }}>
                                    {selectedJob} as of{' '}
                                    {versionInfo?.Version_Start_DateTime
                                        ? moment(versionInfo.Version_Start_DateTime).format('MMM D, YYYY')
                                        : ''}
                                </Text>
                                {selectedJobDetails?.Actual_Start_Date && completionDateForecast ? (
                                    <Text style={{ fontSize: 16, fontWeight: 400 }}>
                                        {moment(selectedJobDetails?.Actual_Start_Date).format('MMM D, YYYY')} -{' '}
                                        {moment(completionDateForecast).format('MMM D, YYYY')}
                                    </Text>
                                ) : null}
                            </View>
                            <View>
                                <Text style={{ fontSize: 20, fontWeight: 600 }}>
                                    {selectedJobDetails?.BU_Description}
                                </Text>
                                <Text>{selectedJobDetails?.AR_AB_Name}</Text>
                            </View>
                        </View>
                    ) : (
                        <View
                            style={{
                                display: 'flex',
                                alignItems: 'stretch',
                                margin: '8px 0',
                                marginLeft: 12,
                                overflow: 'hidden',
                                background: '#F5F5F5',
                                borderRadius: 8,
                                flexDirection: 'row',
                            }}
                        >
                            <View>
                                <Pressable
                                    style={{
                                        background: isHighlightedVersion ? '#0b142e' : '#fff',
                                        border: '2px solid #F5F5F5',
                                        color: isHighlightedVersion ? '#fff' : '#000',
                                        padding: '8px 8px',
                                        height: 36,
                                        width: 140,
                                        textAlign: 'center',
                                        fontSize: '12px',
                                        borderRadius: 8,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    onPress={() => setOpenPicker(openPicker === 'version' ? null : 'version')}
                                >
                                    <Text style={{ color: isHighlightedVersion ? '#fff' : '#000', fontSize: 12 }}>
                                        {selectedVersion
                                            ? dropdownVersion.find((v) => v.value === selectedVersion)?.label
                                            : 'Select Forecast Date'}
                                    </Text>
                                </Pressable>
                                {/* <Pressable
                                    style={{
                                        background: '#3b82f6',
                                        color: '#fff',
                                        padding: '6px 10px',
                                        height: 36,
                                        marginLeft: 8,
                                        borderRadius: 6,
                                        cursor: isRefreshing ? 'not-allowed' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        opacity: isRefreshing ? 0.6 : 1,
                                    }}
                                    onPress={async () => {
                                        if (isRefreshing) return;
                                        setIsRefreshing(true);
                                        try {
                                            const data = await loadVersionTeamJob({ forecastGuid: selectedVersion });
                                            if (data?.version) {
                                                setVersions(data.version);
                                            }
                                            if (data?.teams) {
                                                const sorted = data.teams?.sort((a, b) => {
                                                    if (a.Cat_Code_BU_21 < b.Cat_Code_BU_21) return -1;
                                                    if (a.Cat_Code_BU_21 > b.Cat_Code_BU_21) return 1;
                                                    return 0;
                                                }).filter((team) => team.Cat_Code_BU_21_Description != '.');
                                                setTeams(sorted);
                                            }
                                            if (data?.jobs) {
                                                setJobs(data.jobs);
                                            }
                                            if (data?.jobsClosed) {
                                                setJobsClosed(data.jobsClosed);
                                            }
                                            alert('Cache refreshed successfully!');
                                        } catch (error) {
                                            console.error('Error refreshing cache:', error);
                                            alert('Failed to refresh cache: ' + error.message);
                                        } finally {
                                            setIsRefreshing(false);
                                        }
                                    }}
                                    disabled={isRefreshing}
                                >
                                    <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>
                                        {isRefreshing ? '⟳' : '↻'}
                                    </Text>
                                </Pressable> */}
                            </View>

                            <Pressable
                                style={{
                                    background: '#F5F5F5',
                                    color: '#000',
                                    padding: '8px 8px',
                                    height: 36,
                                    width: 175,
                                    textAlign: 'center',
                                    fontSize: '13px',
                                    opacity: 0.75,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                onPress={() => setOpenPicker(openPicker === 'team' ? null : 'team')}
                            >
                                <Text style={{ fontSize: 12 }}>
                                    {selectedTeam
                                        ? (TEAM_DISPLAY_NAMES[selectedTeam] || dropdownTeam.find((t) => t.value === selectedTeam)?.label)
                                        : 'Select Team'}
                                </Text>
                            </Pressable>

                            <Pressable
                                style={{
                                    padding: 4,
                                    height: 36,
                                    minWidth: 200,
                                    textAlign: 'center',
                                    background: '#F5F5F5',
                                    opacity: 0.75,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                                onPress={() => setOpenPicker(openPicker === 'job' ? null : 'job')}
                            >
                                <Text style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {selectedJob
                                        ? (dropdownJobs.find((j) => j.value === selectedJob) || dropdownJobsInventory.find((j) => j.value === selectedJob) || dropdownJobsClosed.find((j) => j.value === selectedJob))?.label
                                        : 'Select Job'}
                                </Text>
                                <Text style={{ fontSize: 10 }}>
                                    {selectedJobDetails ? selectedJobDetails?.AR_AB_Name : ''}
                                </Text>
                            </Pressable>

                            {!props.photo ? (
                                <Pressable
                                    style={{ padding: '8px 8px', height: 36, background: '#F5F5F5', opacity: 0.75, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    onPress={() => setExpandedJob((v) => !v)}
                                >
                                    <Text>⋮</Text>
                                </Pressable>
                            ) : null}
                        </View>
                    )}

                    {!props.photo ? <DateRangePicker /> : null}

                    {selectedJob && !props.photo ? (
                        <Pressable
                            onPress={createExcel}
                            disabled={downloadingExcel}
                            style={{
                                border: '1px solid #D9D9D9',
                                borderRadius: 8,
                                padding: '6px',
                                height: 36,
                                width: 36,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: downloadingExcel ? '#f5f5f5' : '#fff',
                                cursor: downloadingExcel ? 'wait' : 'pointer',
                                opacity: downloadingExcel ? 0.6 : 1,
                            }}
                        >
                            {downloadingExcel ? (
                                <Text style={{ fontSize: 12 }}>⏳</Text>
                            ) : (
                                <Image source={download} style={{ width: 18, height: 18 }} />
                            )}
                        </Pressable>
                    ) : null}

                    {/* Admin "View as role" switcher — see previewRole /
                        effectiveIsProjectCoordinator (db/company.js). */}
                    {canPreview && !props.photo ? (
                        <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, border: `1px solid ${previewRole ? '#065F46' : '#D9D9D9'}`, borderRadius: 8, height: 36, padding: '0 8px', background: '#fff' }}>
                            <Text style={{ fontSize: 9, color: '#94A3B8', fontWeight: '700', letterSpacing: 1, whiteSpace: 'nowrap' }}>VIEW AS</Text>
                            <select
                                value={previewRole || ''}
                                onChange={(e) => applyPreviewRole(e.target.value)}
                                style={{ height: 24, fontSize: 12, fontWeight: 600, padding: '0 4px', border: 'none', color: previewRole ? '#065F46' : '#475569', backgroundColor: '#fff', cursor: 'pointer', outline: 'none' }}
                            >
                                {previewableRoles.map((r) => (
                                    <option key={r.value || 'self'} value={r.value}>{r.label}</option>
                                ))}
                            </select>
                        </View>
                    ) : null}

                    {/* PCL forecast lock — only on the live version (older versions
                        are already locked) and only for Project Controls Leads. */}
                    {isPCL && selectedJob && selectedVersion && isLiveVersion && !props.photo ? (
                        <Pressable
                            onPress={toggleJobLock}
                            style={{
                                border: `1px solid ${jobLocked ? '#0b142e' : '#D9D9D9'}`,
                                borderRadius: 8,
                                padding: '0 12px',
                                height: 36,
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 6,
                                background: jobLocked ? '#0b142e' : '#fff',
                                cursor: 'pointer',
                            }}
                            title={jobLocked
                                ? 'Forecast is locked for this version — click to unlock'
                                : 'Lock this forecast so it stays frozen until the PSR'}
                        >
                            <Text style={{ fontSize: 14, color: jobLocked ? '#fff' : '#444' }}>
                                {jobLocked ? '■' : '□'}
                            </Text>
                            <Text style={{ fontSize: 12, fontWeight: 600, color: jobLocked ? '#fff' : '#444' }}>
                                {jobLocked ? 'Forecast Locked' : 'Lock Forecast'}
                            </Text>
                        </Pressable>
                    ) : null}

                    {/* Admin version unlock — admins get it on any version but the
                        live one; everyone else only sees the tag while it's open. */}
                    {selectedJob && selectedVersion && !props.photo && (versionUnlock || (isAdmin && !isLiveVersion)) ? (
                        <View
                            title={versionUnlock
                                ? `Unlocked by ${versionUnlock.by || 'an admin'} until ${moment(versionUnlock.until).format('h:mm A')}. Edits stay on ${versionLabel}; they don't carry into the live version.`
                                : `${versionLabel} is locked. Admins can unlock it for this job or all jobs until 11:59 PM tonight.`}
                            style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6, border: `1px solid ${versionUnlock ? '#B45309' : '#D9D9D9'}`, borderRadius: 8, height: 36, padding: '0 8px', background: versionUnlock ? '#FFFBEB' : '#fff' }}
                        >
                            <Text style={{ fontSize: 9, color: versionUnlock ? '#B45309' : '#94A3B8', fontWeight: '700', letterSpacing: 1 }}>
                                {versionUnlock ? 'UNLOCKED' : 'VERSION'}
                            </Text>
                            {isAdmin ? (
                                <select
                                    value={unlockScope}
                                    onChange={(e) => setUnlockScope(e.target.value)}
                                    style={{ height: 24, fontSize: 12, fontWeight: 600, padding: '0 4px', border: 'none', color: versionUnlock ? '#B45309' : '#475569', backgroundColor: 'transparent', cursor: 'pointer', outline: 'none' }}
                                >
                                    <option value="locked">Locked</option>
                                    <option value="job">This job</option>
                                    <option value="all">All jobs</option>
                                </select>
                            ) : null}
                            {versionUnlock ? (
                                <Text style={{ fontSize: 12, fontWeight: 600, color: '#B45309' }}>
                                    {`until ${moment(versionUnlock.until).format('h:mm A')}`}
                                </Text>
                            ) : null}
                        </View>
                    ) : null}
                </View>

                {/* Right side - indicators */}
                <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    {/* API Status Indicator - Circle */}
                    {!props.photo && selectedJob && selectedVersion ? (
                        <ApiStatusIndicator 
                            selectedVersion={selectedVersion} 
                            selectedJob={selectedJobDetails} 
                            apiStatus={apiStatus} 
                        />
                    ) : null}

                    {/* User Profile */}
                    {!props.photo ? (
                        <UserProfile />
                    ) : null}
                </View>
            </View>

            <View
                style={{
                    borderBottom: `2px solid ${props.photo ? 'white' : '#eaeced'}`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    background: '#fff',
                    flexWrap: 'wrap',
                    position: 'relative',
                }}
            >
                {openPicker === 'version' ? (
                    <PickerPopover left={18}>
                    <View style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, width: 'min(900px, 80vw)' }}>
                        {dropdownVersion.map((version) => (
                            <Pressable
                                key={version.value}
                                onPress={() => {
                                    setOpenPicker(null);
                                    // Clear cached data when version changes
                                    setJobOpenCommitments([]);
                                    setJobUserData([]);
                                    setJobCostDetail([]);
                                    setJobCostDetailCondensed([]);
                                    setK2Data(null);
                                    setJobHEETData(null);

                                    setSelectedVersion(version.value);
                                    setSelectedVersionStorage(version.value);
                                    console.log('cleared out')
                                }}
                                style={{
                                    padding: '10px 4px',
                                    borderRadius: 5,
                                    border: '1px solid #cfd3d5',
                                    borderRightWidth: version.label?.includes('WIP') ? 5 : 1,
                                    textAlign: 'center',
                                    letterSpacing: 2,
                                    fontSize: 12,
                                    background: '#fff',
                                }}
                            >
                                <Text>{version.label}</Text>
                            </Pressable>
                        ))}
                    </View>
                    </PickerPopover>
                ) : null}

                {openPicker === 'team' && teams && teams.length > 0 ? (
                    <PickerPopover left={170}>
                    <View style={{ maxWidth: 'min(760px, 80vw)' }}>
                        {(() => {
                            // Define the specific order with renamed teams
                            const teamOrder = [
                                ['NOR', 'EASTERN', 'MCLEANS'],
                                ['SWT', 'GULF COAST', 'PICKETT'],
                                ['ETX', 'ED']
                            ];
                            
                            const displayNames = TEAM_DISPLAY_NAMES;
                            
                            return teamOrder.map((row, rowIndex) => (
                                <View key={`row-${rowIndex}`} style={{ display: 'flex', flexDirection: 'row', gap: 12, marginBottom: 12 }}>
                                    {row.map((teamCode) => {
                                        const team = dropdownTeam.find(t => t.value === teamCode);
                                        if (!team) return null;
                                        
                                        return (
                                            <Pressable
                                                key={team.value}
                                                onPress={() => {
                                                    setOpenPicker(null);
                                                    setSelectedTeam(team.value);
                                                    setSelectedTeamStorage(team.value);
                                                    setSelectedJob(null);
                                                }}
                                                style={{
                                                    padding: '10px 4px',
                                                    borderRadius: 5,
                                                    border: '1px solid #cfd3d5',
                                                    textAlign: 'center',
                                                    letterSpacing: 2,
                                                    fontSize: 12,
                                                    background: '#fff',
                                                    minWidth: 150,
                                                }}
                                            >
                                                <Text>{displayNames[team.value] || team.label}</Text>
                                            </Pressable>
                                        );
                                    })}
                                </View>
                            ));
                        })()}
                    </View>
                    </PickerPopover>
                ) : null}

                {openPicker === 'job' && selectedTeam ? (
                    <PickerPopover left={357}>
                    <View style={{ maxWidth: 'min(900px, 80vw)' }}>
                        <View style={{ position: 'relative', width: 300 }}>
                            <TextInput
                                value={searchJob}
                                onChangeText={(text) => setSearchJob(text)}
                                placeholder="Search Jobs"
                                style={{
                                    padding: 10,
                                    paddingRight: searchJob ? 60 : 35,
                                    borderRadius: 5,
                                    marginBottom: 20,
                                    width: '100%',
                                    border: '1px solid #cfd3d5',
                                }}
                            />
                            {searchJob ? (
                                <Pressable
                                    onPress={() => setSearchJob('')}
                                    style={{
                                        position: 'absolute',
                                        right: 35,
                                        top: 10,
                                        cursor: 'pointer',
                                        padding: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 20,
                                        height: 20,
                                        borderRadius: 10,
                                        background: '#e0e0e0',
                                    }}
                                >
                                    <Text style={{ color: '#666', fontSize: 12, lineHeight: 1 }}>✕</Text>
                                </Pressable>
                            ) : null}
                            <Text
                                style={{
                                    position: 'absolute',
                                    right: 10,
                                    top: 12,
                                    color: 'grey',
                                }}
                            >
                                🔎
                            </Text>
                        </View>

                        <View style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 4, maxWidth: pageWidth - 300 }}>
                            {(searchableJobs || []).map((job) => (
                                <Pressable
                                    key={job.BU_ID}
                                    onPress={() => {
                                        // Clear cached data when job changes
                                        setJobOpenCommitments([]);
                                        setJobUserData([]);
                                        setJobCostDetail([]);
                                        setJobCostDetailCondensed([]);
                                        setK2Data(null);
                                        setJobHEETData(null);

                                        setClosedJobMode(false);
                                        setOpenPicker(null);
                                        setSelectedJob(job.BU_ID);
                                        setSelectedJobDetails(job);
                                        setSelectedJobDetailsStorage(job);
                                        setSelectedJobStorage(job.BU_ID);
                                        if (job.Cat_Code_BU_18 == '3') setRegular('phased');
                                        else setRegular('regular');
                                    }}
                                    style={{
                                        padding: '4px 4px',
                                        borderRadius: 5,
                                        display: 'flex',
                                        alignItems: 'left',
                                        gap: 4,
                                        border: '1px solid #cfd3d5',
                                        background: '#fff',
                                        width: 150
                                    }}
                                >
                                    <Text style={{ fontSize: 14, width: 60 }}>{job.BU_ID}</Text>
                                    <Text style={{ fontSize: 10, textAlign: 'left', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.BU_Description}</Text>
                                    {/* <Text
                                        style={{
                                            borderRadius: 5,
                                            padding: '3px 6px',
                                            fontSize: 11,
                                            background: '#ececec',
                                            textAlign: 'left',
                                        }}
                                    >
                                        {job.Cat_Code_BU_16 == 'A' ? 'ACTIVE' : job.Cat_Code_BU_16 == 'P' ? 'PENDING' : ''}
                                    </Text> */}
                                </Pressable>
                            ))}

                            {isPickettTeam ? (
                                <View style={{ marginTop: 20, width: '100%' }}>
                                    <Text style={{ fontSize: 14, fontWeight: '600', marginBottom: 10, color: '#0b142e' }}>
                                        Inventory Jobs {inventoryLoading ? '(loading…)' : `(${dropdownJobsInventory.length})`}
                                    </Text>

                                    <View style={{ position: 'relative', width: 300, marginBottom: 10 }}>
                                        <TextInput
                                            value={searchInventoryJob}
                                            onChangeText={(text) => setSearchInventoryJob(text)}
                                            placeholder="Search Inventory Jobs"
                                            style={{
                                                padding: 10,
                                                borderRadius: 5,
                                                width: '100%',
                                                border: '1px solid #cfd3d5',
                                                fontSize: 12,
                                            }}
                                        />
                                        <Text
                                            style={{
                                                position: 'absolute',
                                                right: 10,
                                                top: 12,
                                                color: 'grey',
                                            }}
                                        >
                                            🔎
                                        </Text>
                                    </View>

                                    {searchInventoryJob ? (
                                        <View style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 4, maxHeight: 300, overflowY: 'auto' }}>
                                            {(searchableInventoryJobs || []).map((job) => (
                                                <Pressable
                                                    key={job.value}
                                                    onPress={() => {
                                                        setJobOpenCommitments([]);
                                                        setJobUserData([]);
                                                        setJobCostDetail([]);
                                                        setJobCostDetailCondensed([]);
                                                        setK2Data(null);
                                                        setJobHEETData(null);

                                                        setClosedJobMode(job.data?.Job_Status === 'C');
                                                        setSelectedJob(job.value);
                                                        setSelectedJobDetails(job.data);
                                                        setSelectedJobDetailsStorage(job.data);
                                                        setSelectedJobStorage(job.value);

                                                        if (job.data?.Cat_Code_BU_18 == '3') setRegular('phased');
                                                        else setRegular('regular');
                                                    }}
                                                    style={{
                                                        padding: '4px 4px',
                                                        borderRadius: 5,
                                                        display: 'flex',
                                                        alignItems: 'left',
                                                        gap: 4,
                                                        border: '1px solid #cfd3d5',
                                                        background: '#eef5ff',
                                                        width: 150,
                                                    }}
                                                >
                                                    <Text style={{ fontSize: 14, width: 60 }}>{job.data.BU_ID}</Text>
                                                    <Text style={{ fontSize: 10, textAlign: 'left', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {job.data.BU_Description}
                                                    </Text>
                                                </Pressable>
                                            ))}
                                        </View>
                                    ) : null}
                                </View>
                            ) : null}

                            {dropdownJobsClosed.length > 0 ? (
                                <View style={{ marginTop: 20 }}>
                                    <Text style={{ fontSize: 14, fontWeight: '600', marginBottom: 10, color: '#0b142e' }}>
                                        Closed Jobs
                                    </Text>
                                    
                                    <View style={{ position: 'relative', width: 300, marginBottom: 10 }}>
                                        <TextInput
                                            value={searchClosedJob}
                                            onChangeText={(text) => setSearchClosedJob(text)}
                                            placeholder="Search Closed Jobs"
                                            style={{
                                                padding: 10,
                                                borderRadius: 5,
                                                width: '100%',
                                                border: '1px solid #cfd3d5',
                                                fontSize: 12,
                                            }}
                                        />
                                        <Text
                                            style={{
                                                position: 'absolute',
                                                right: 10,
                                                top: 12,
                                                color: 'grey',
                                            }}
                                        >
                                            🔎
                                        </Text>
                                    </View>

                                    {searchClosedJob ? (
                                        <View style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 4, maxHeight: 300, overflowY: 'auto' }}>
                                            {(searchableClosedJobs || []).map((job) => (
                                            <Pressable
                                                key={job.value}
                                                onPress={() => {
                                                    // Clear cached data when job changes
                                                    setJobOpenCommitments([]);
                                                    setJobUserData([]);
                                                    setJobCostDetail([]);
                                                    setJobCostDetailCondensed([]);
                                                    setK2Data(null);
                                                    setJobHEETData(null);

                                                    setClosedJobMode(true);
                                                    setSelectedJob(job.value);
                                                    setSelectedJobDetails(job.data);
                                                    setSelectedJobDetailsStorage(job.data);
                                                    setSelectedJobStorage(job.value);

                                                    if (job.data.Cat_Code_BU_18 == '3') setRegular('phased');
                                                    else setRegular('regular');
                                                }}
                                                style={{
                                                    padding: '4px 4px',
                                                    borderRadius: 5,
                                                    display: 'flex',
                                                    alignItems: 'left',
                                                    gap: 4,
                                                    border: '1px solid #cfd3d5',
                                                    background: '#f0f0f0',
                                                    width: 150,
                                                }}
                                            >
                                                <Text style={{ fontSize: 14, width: 60 }}>{job.data.BU_ID}</Text>
                                                <Text style={{ fontSize: 10, textAlign: 'left', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {job.data.BU_Description}
                                                </Text>
                                            </Pressable>
                                        ))}
                                        </View>
                                    ) : null}
                                </View>
                            ) : null}
                        </View>

                        {!jobSections[selectedTeam] ? (
                            <Text style={{ color: '#777', fontSize: 12, textAlign: 'center', marginTop: 20 }}>
                                No Jobs Found
                            </Text>
                        ) : null}
                    </View>
                    </PickerPopover>
                ) : null}

                {expandedJob ? (
                    <ViewTransition>
                        <View style={{ padding: 10, paddingLeft: 20, width: '100%', maxWidth: 900 }}>
                            <View style={styles.infoRow}>
                                <Text style={styles.span}>Location</Text>
                                <Text style={styles.span}>{selectedJobDetails?.Address_State_Name}</Text>
                            </View>

                            <View style={styles.infoRow}>
                                <Text style={styles.span}>Contract Type</Text>
                                <Text style={styles.span}>{selectedJobDetails?.Cat_Code_BU_08_Description}</Text>
                            </View>

                            <View style={styles.infoRow}>
                                <Text style={styles.span}>Project Manager</Text>
                                <Text style={styles.span}>{selectedJobDetails?.Address_2_AB_Name}</Text>
                            </View>

                            <View style={styles.infoRow}>
                                <Text style={styles.span}>Start Date - Estimate</Text>
                                <Text style={styles.span}>
                                    {selectedJobDetails?.Actual_Start_Date
                                        ? moment(selectedJobDetails?.Actual_Start_Date).format('YYYY-MM-DD')
                                        : ''}
                                </Text>
                            </View>

                            <View style={styles.infoRow}>
                                <Text style={styles.span}>Start Date - Actual</Text>
                                <DateInput
                                    value={startDateActual}
                                    onValueChange={(d) => {
                                        setStartDateActual(d);
                                        saveObject('startDateActual', d);
                                        setStartDateActualGlobal(d);
                                    }}
                                />
                            </View>

                            <View style={styles.infoRow}>
                                <Text style={styles.span}>Completion Date - Original</Text>
                                <Text style={styles.span}>
                                    {selectedJobDetails?.Planned_Complete_Date
                                        ? moment(selectedJobDetails?.Planned_Complete_Date).format('YYYY-MM-DD')
                                        : ''}
                                </Text>
                            </View>

                            <View style={styles.infoRow}>
                                <Text style={styles.span}>Complete Date - Forecast</Text>
                                <DateInput
                                    value={completionDateForecast}
                                    onValueChange={(d) => {
                                        setCompletionDateForecast(d);
                                        setProjectComplete(d);
                                        saveObject('completionDateForecast', d);
                                    }}
                                />
                            </View>

                            <View style={styles.infoRow}>
                                <Text style={styles.span}>Mechanically Complete</Text>
                                <DateInput
                                    value={mechanicallyComplete}
                                    onValueChange={(d) => {
                                        setMechanicallyComplete(d);
                                        saveObject('mechanicallyComplete', d);
                                        setMechanicallyCompleteGlobal(d);
                                    }}
                                />
                            </View>

                            <View style={styles.infoRow}>
                                <Text style={styles.span}>Phased Forecasting</Text>
                                <Text style={styles.span}>
                                    {selectedJobDetails?.Cat_Code_BU_18 == '3' ? 'Phased' : 'Cost Type'}
                                </Text>
                            </View>

                            <View style={styles.infoRow}>
                                <Text style={styles.span}>Using WIP Date</Text>
                                <Text style={styles.span}>
                                    {selectedWIP?.Version_Start_DateTime
                                        ? moment(selectedWIP?.Version_Start_DateTime).format('YYYY-MM-DD')
                                        : ''}
                                </Text>
                            </View>
                        </View>
                    </ViewTransition>
                ) : null}
            </View>
        </View>
    );
}


